import {
  HttpTransportType,
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  type IHttpConnectionOptions,
  LogLevel,
} from "@microsoft/signalr";

/** Public connection state — re-exported simplified shape of SignalR's enum. */
export type ConnectionState = "disconnected" | "connecting" | "connected" | "reconnecting";

export type SnapshotHandler<T> = (snapshot: T) => void;
export type ConnectionStateHandler = (state: ConnectionState) => void;

export interface RealtimeClientOptions {
  /** Full URL of the SignalR hub, e.g. `https://api.mesterplan.dk/hubs/realtime`. */
  hubUrl: string;
  /**
   * Supplies the current access token. Omit it when the transport authenticates
   * with cookies instead of browser-held bearer tokens.
   */
  getToken?: () => Promise<string | null> | string | null;
  /** Whether cross-origin requests should include cookies. */
  withCredentials?: boolean;
  /** Override the SignalR log level. Defaults to `Warning`. */
  logLevel?: LogLevel;
  /**
   * Test seam — inject a pre-built HubConnection. The default builds one with
   * either cookie transport or a bearer-token interceptor plus automatic reconnect.
   */
  buildConnection?: (opts: RealtimeClientOptions) => HubConnection;
}

export interface Subscription {
  readonly channelKey: string;
  unsubscribe(): Promise<void>;
}

/**
 * Server method name the hub uses to push snapshots — must stay in sync with
 * the backend's `RealtimePublisher.SnapshotMethod`.
 */
const SNAPSHOT_METHOD = "snapshot";

/**
 * Backoff intervals (ms) handed to SignalR's automaticReconnect. Capped so the
 * client keeps trying after long disconnects without burning the device's
 * battery / data quota.
 */
const RECONNECT_DELAYS_MS = [0, 1_000, 2_000, 5_000, 10_000, 15_000, 30_000];

export class RealtimeClient {
  private connection: HubConnection;
  private readonly subscriptions = new Map<string, Set<SnapshotHandler<unknown>>>();
  private readonly stateHandlers = new Set<ConnectionStateHandler>();
  private lastEmittedState: ConnectionState | null = null;

  constructor(options: RealtimeClientOptions) {
    this.connection = (options.buildConnection ?? defaultConnectionBuilder)(options);
    this.wireConnectionEvents();
  }

  private wireConnectionEvents(): void {
    this.connection.onreconnecting(() => this.emitState("reconnecting"));
    this.connection.onreconnected(() => this.emitState("connected"));
    this.connection.onclose(() => this.emitState("disconnected"));
    this.connection.on(SNAPSHOT_METHOD, (channelKey: string, payload: unknown) => {
      const handlers = this.subscriptions.get(channelKey);
      if (!handlers) return;
      for (const h of handlers) {
        try {
          h(payload);
        } catch (err) {
          console.error("[realtime] snapshot handler threw", err);
        }
      }
    });
  }

  /** Idempotent — calling `start` after the hub is already connected is a no-op. */
  async start(): Promise<void> {
    if (this.connection.state === HubConnectionState.Connected) return;
    if (this.connection.state === HubConnectionState.Connecting) return;
    this.emitState("connecting");
    await this.connection.start();
    this.emitState("connected");
  }

  async stop(): Promise<void> {
    if (this.connection.state === HubConnectionState.Disconnected) return;
    await this.connection.stop();
    this.emitState("disconnected");
  }

  /**
   * Subscribes to a channel. The server prefixes the caller's membership-checked
   * active org, so pass the channel key without it (e.g. `walkthroughs/9c2e…`).
   *
   * Returns a `Subscription` whose `unsubscribe()` removes only this specific
   * handler — other handlers on the same channel keep receiving snapshots
   * until the last one unsubscribes, at which point the client tells the
   * server to drop the group.
   */
  async subscribe<T>(channelKey: string, handler: SnapshotHandler<T>): Promise<Subscription> {
    const handlers = this.subscriptions.get(channelKey) ?? new Set<SnapshotHandler<unknown>>();
    const wasFirst = handlers.size === 0;
    handlers.add(handler as SnapshotHandler<unknown>);
    this.subscriptions.set(channelKey, handlers);

    if (wasFirst) {
      // Only call Subscribe on the server for the first handler — subsequent
      // local handlers piggyback on the existing group membership.
      await this.connection.invoke("Subscribe", channelKey);
    }

    return {
      channelKey,
      unsubscribe: async () => {
        const set = this.subscriptions.get(channelKey);
        if (!set) return;
        set.delete(handler as SnapshotHandler<unknown>);
        if (set.size === 0) {
          this.subscriptions.delete(channelKey);
          if (this.connection.state === HubConnectionState.Connected) {
            await this.connection.invoke("Unsubscribe", channelKey);
          }
        }
      },
    };
  }

  /** Subscribe to connection-state changes — useful for showing online/offline UI. */
  onStateChange(handler: ConnectionStateHandler): () => void {
    this.stateHandlers.add(handler);
    return () => this.stateHandlers.delete(handler);
  }

  /** Current connection state. */
  get state(): ConnectionState {
    switch (this.connection.state) {
      case HubConnectionState.Connected:
        return "connected";
      case HubConnectionState.Connecting:
        return "connecting";
      case HubConnectionState.Reconnecting:
        return "reconnecting";
      default:
        return "disconnected";
    }
  }

  /** Test-only accessor for the underlying HubConnection. */
  get _connectionForTest(): HubConnection {
    return this.connection;
  }

  private emitState(state: ConnectionState): void {
    // De-duplicate consecutive identical states — SignalR's onclose fires
    // alongside our explicit emit inside stop(), and we don't want listeners
    // to see "disconnected" twice in a row.
    if (state === this.lastEmittedState) return;
    this.lastEmittedState = state;
    for (const h of this.stateHandlers) {
      try {
        h(state);
      } catch (err) {
        console.error("[realtime] state handler threw", err);
      }
    }
  }
}

function defaultConnectionBuilder(opts: RealtimeClientOptions): HubConnection {
  const connectionOptions: IHttpConnectionOptions = {
    transport: HttpTransportType.WebSockets | HttpTransportType.LongPolling,
  };

  if (opts.withCredentials !== undefined) {
    connectionOptions.withCredentials = opts.withCredentials;
  }

  if (opts.getToken) {
    connectionOptions.accessTokenFactory = async () => {
      const tok = await opts.getToken?.();
      return tok ?? "";
    };
  }

  return new HubConnectionBuilder()
    .withUrl(opts.hubUrl, connectionOptions)
    .withAutomaticReconnect(RECONNECT_DELAYS_MS)
    .configureLogging(opts.logLevel ?? LogLevel.Warning)
    .build();
}
